import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';

export interface SearchItem {
  id: string;
  name: string;
  description?: string;
  secondaryText?: string;
  price?: number;
  isTaxable?: boolean;
  [key: string]: any; // Allow for additional properties
}

interface SearchDropdownProps {
  items: SearchItem[];
  value: string;
  onChange: (value: string) => void;
  onSelect: (item: SearchItem) => void;
  onAddNew?: () => void;
  onCustomInput?: (value: string) => void; // For free text input
  addNewLabel?: string;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  renderItem?: (item: SearchItem) => React.ReactNode;
  filterFunction?: (item: SearchItem, searchTerm: string) => boolean;
}

export function SearchDropdown({
  items,
  value,
  onChange,
  onSelect,
  onAddNew,
  onCustomInput,
  addNewLabel = 'Add New',
  placeholder = 'Search...',
  emptyMessage = 'No items found',
  disabled = false,
  className = '',
  renderItem,
  filterFunction
}: SearchDropdownProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [filteredItems, setFilteredItems] = useState<SearchItem[]>(items);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate dropdown position based on input element
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      setDropdownPosition({
        top: rect.bottom + scrollTop + 4, // 4px gap below input
        left: rect.left + scrollLeft,
        width: rect.width
      });
    }
  };

  // Update position when focused
  useEffect(() => {
    if (isFocused) {
      updateDropdownPosition();

      // Update position on scroll/resize
      const handleReposition = () => updateDropdownPosition();
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);

      return () => {
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [isFocused]);

  // Default filter function if none provided
  const defaultFilterFunction = (item: SearchItem, searchTerm: string) => {
    const term = searchTerm.toLowerCase();
    return (
      item.name.toLowerCase().includes(term) ||
      (item.description && item.description.toLowerCase().includes(term)) ||
      (item.secondaryText && item.secondaryText.toLowerCase().includes(term))
    );
  };

  // Update filtered items when search term or items change
  useEffect(() => {
    const filter = filterFunction || defaultFilterFunction;
    const filtered = value.trim() 
      ? items.filter(item => filter(item, value))
      : items;
    
    setFilteredItems(filtered);
    
    // Reset selected index when filtered items change
    if (filtered.length > 0) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(-1);
    }
  }, [value, items, filterFunction]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isFocused) return;

    switch (e.key) {
      case 'ArrowDown':
        if (filteredItems.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredItems.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        if (filteredItems.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : filteredItems.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredItems.length) {
          // Select from dropdown
          onSelect(filteredItems[selectedIndex]);
        } else if (onCustomInput && value.trim()) {
          // Use custom text input if no item selected and custom input is supported
          onCustomInput(value.trim());
          setIsFocused(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsFocused(false);
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && isFocused) {
      const selectedElement = document.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, isFocused]);

  // Default item renderer
  const defaultRenderItem = (item: SearchItem, index: number) => (
    <div
      key={item.id}
      data-index={index}
      className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-0 ${
        selectedIndex === index ? 'bg-gray-100 dark:bg-gray-700' : ''
      }`}
      onClick={() => onSelect(item)}
    >
      <div className="font-medium">{item.name}</div>
      {item.description && (
        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {item.description}
        </div>
      )}
      {item.price !== undefined && (
        <div className="flex justify-between items-center mt-1">
          <div className="text-sm font-medium text-green-600">
            ${item.price.toFixed(2)}
          </div>
          {item.isTaxable && (
            <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
              Taxable
            </span>
          )}
        </div>
      )}
      {item.secondaryText && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {item.secondaryText}
        </div>
      )}
    </div>
  );

  // Create dropdown content
  const dropdownContent = isFocused ? (
    <div
      className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        minWidth: '200px'
      }}
      ref={dropdownRef}
    >
      {filteredItems.length > 0 ? (
        <>
          {filteredItems.map((item, index) =>
            renderItem ? renderItem(item) : defaultRenderItem(item, index)
          )}
          {onAddNew && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-2">
              <button
                className="w-full p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md text-sm text-primary flex items-center gap-2"
                onClick={onAddNew}
              >
                <Plus className="h-4 w-4" />
                {addNewLabel}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="p-3">
          <div className="text-gray-500 text-sm mb-2">{emptyMessage}</div>
          {onAddNew && (
            <button
              className="w-full p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md text-sm text-primary flex items-center gap-2"
              onClick={onAddNew}
            >
              <Plus className="h-4 w-4" />
              {addNewLabel}
            </button>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay hiding the dropdown to allow for selection
            setTimeout(() => {
              setIsFocused(false);
              // If user typed text but didn't select anything, treat as custom input
              if (onCustomInput && value.trim() && selectedIndex === -1) {
                const exactMatch = filteredItems.find(item =>
                  item.name.toLowerCase() === value.trim().toLowerCase()
                );
                if (!exactMatch) {
                  onCustomInput(value.trim());
                }
              }
            }, 200);
          }}
          onKeyDown={handleKeyDown}
          className={`${className}`}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}
